'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { MapPin, Users, ArrowLeft, CheckCircle, Clock, FileText, Building2, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import HomepageNav from '../../components/HomepageNav';
import HomepageFooter from '../../components/HomepageFooter';

const projectsData: Record<string, {
  id: string;
  name: string;
  location: string;
  country: string;
  type: string;
  totalAmount: number;
  fundedAmount: number;
  investors: number;
  status: string;
  description: string;
  developer: {name: string;company: string;verified: boolean;projects: number;};
  images: Array<{src: string;alt: string;}>;
  milestones: Array<{title: string;percentage: number;status: string;date: string;}>;
  documents: Array<{name: string;type: string;}>;
  escrowStatus: string;
}> = {
  'proj-1': {
    id: 'proj-1',
    name: 'Lekki Phase 2 Residences',
    location: 'Lagos, Nigeria',
    country: 'Nigeria',
    type: 'Residential',
    totalAmount: 2500000,
    fundedAmount: 1750000,
    investors: 34,
    status: 'Active',
    description: 'A premium residential development in the heart of Lekki Phase 2, offering 48 modern apartments across 4 blocks. The project features smart home integration, 24/7 security, and landscaped communal areas. All units are designed to international standards with high-quality finishes.',
    developer: { name: 'Emeka Okafor', company: 'Okafor Properties Ltd', verified: true, projects: 7 },
    images: [
    { src: "https://images.unsplash.com/photo-1687771774267-6319677a0994", alt: 'Modern residential apartment complex exterior view in Lagos Nigeria' },
    { src: "https://images.unsplash.com/photo-1635343509492-0347e539e4d3", alt: 'Interior lobby of residential building with modern finishes' },
    { src: "https://img.rocket.new/generatedImages/rocket_gen_img_102d9c699-1774374063112.png", alt: 'Aerial view of residential estate with landscaped gardens' }],

    milestones: [
    { title: 'Foundation & Groundwork', percentage: 20, status: 'Released', date: 'Jan 2026' },
    { title: 'Structural Frame (Floors 1-4)', percentage: 25, status: 'Released', date: 'Mar 2026' },
    { title: 'Roofing & External Walls', percentage: 20, status: 'Inspector Approved', date: 'May 2026' },
    { title: 'Internal Fit-Out', percentage: 20, status: 'Awaiting Inspection', date: 'Jul 2026' },
    { title: 'Final Completion & Handover', percentage: 15, status: 'Pending Start', date: 'Sep 2026' }],

    documents: [
    { name: 'Title Deed', type: 'Legal' },
    { name: 'Building Permit', type: 'Regulatory' },
    { name: 'Environmental Impact Assessment', type: 'Compliance' },
    { name: 'Architect Plans', type: 'Technical' }],

    escrowStatus: 'Active — 3 of 5 milestones funded'
  },
  'proj-2': {
    id: 'proj-2',
    name: 'Westlands Commercial Tower',
    location: 'Nairobi, Kenya',
    country: 'Kenya',
    type: 'Commercial',
    totalAmount: 5000000,
    fundedAmount: 2100000,
    investors: 18,
    status: 'Funding',
    description: 'A 12-storey Grade A commercial office tower in Westlands, Nairobi\'s premier business district. The development offers flexible floor plates, LEED-certified design, and state-of-the-art facilities including a rooftop terrace, conference center, and underground parking.',
    developer: { name: 'Amina Wanjiku', company: 'Wanjiku Developments', verified: true, projects: 4 },
    images: [
    { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1513eb0d1-1772893767590.png", alt: 'Commercial office tower under construction in Nairobi Kenya Westlands district' },
    { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1a8315c7a-1777286375580.png", alt: 'Office building interior with open plan workspace and modern design' }],

    milestones: [
    { title: 'Site Preparation & Foundation', percentage: 15, status: 'Released', date: 'Feb 2026' },
    { title: 'Basement & Parking Structure', percentage: 20, status: 'Awaiting Inspection', date: 'May 2026' },
    { title: 'Core & Shell (Floors 1-6)', percentage: 25, status: 'Pending Start', date: 'Aug 2026' },
    { title: 'Core & Shell (Floors 7-12)', percentage: 25, status: 'Pending Start', date: 'Nov 2026' },
    { title: 'Fit-Out & Commissioning', percentage: 15, status: 'Pending Start', date: 'Feb 2027' }],

    documents: [
    { name: 'Land Title', type: 'Legal' },
    { name: 'NCA Approval', type: 'Regulatory' },
    { name: 'Structural Engineering Report', type: 'Technical' }],

    escrowStatus: 'Active — Funding in progress'
  }
};

const defaultProject = projectsData['proj-1'];

function getMilestoneColor(status: string) {
  switch (status) {
    case 'Released':return 'text-secondary bg-secondary/8 border-secondary/20';
    case 'Inspector Approved':return 'text-primary bg-primary/8 border-primary/20';
    case 'Awaiting Inspection':return 'text-warning bg-warning/8 border-warning/20';
    case 'Rework Required':return 'text-danger bg-danger/8 border-danger/20';
    default:return 'text-muted-foreground bg-muted border-border';
  }
}

function formatCurrency(amount: number): string {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount}`;
}

export default function ProjectDetailPage({ params }: {params: {id: string;};}) {
  const project = projectsData[params?.id] || defaultProject;
  const [activeImage, setActiveImage] = useState(0);
  const progress = Math.round(project.fundedAmount / project.totalAmount * 100);

  return (
    <div className="min-h-screen bg-white text-foreground">
      <HomepageNav />
      <main className="pt-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link href="/marketplace" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors">
              <ArrowLeft size={14} />
              Marketplace
            </Link>
            <span>/</span>
            <span className="text-foreground font-medium">{project.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image Gallery */}
              <div className="rounded-xl overflow-hidden border border-border">
                <div className="relative h-72 md:h-96 bg-muted">
                  <img
                    src={project.images[activeImage]?.src}
                    alt={project.images[activeImage]?.alt}
                    className="w-full h-full object-cover" />
                  
                  {project.images.length > 1 &&
                  <>
                      <button
                      onClick={() => setActiveImage((prev) => (prev - 1 + project.images.length) % project.images.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 border border-border flex items-center justify-center hover:bg-white transition-colors shadow-sm"
                      aria-label="Previous image">
                      
                        <ChevronLeft size={16} />
                      </button>
                      <button
                      onClick={() => setActiveImage((prev) => (prev + 1) % project.images.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 border border-border flex items-center justify-center hover:bg-white transition-colors shadow-sm"
                      aria-label="Next image">
                      
                        <ChevronRight size={16} />
                      </button>
                    </>
                  }
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {project.images.map((_, i) =>
                    <button
                      key={i}
                      onClick={() => setActiveImage(i)}
                      className={`w-2 h-2 rounded-full transition-colors ${i === activeImage ? 'bg-white' : 'bg-white/50'}`}
                      aria-label={`View image ${i + 1}`} />

                    )}
                  </div>
                </div>
                {project.images.length > 1 &&
                <div className="flex gap-2 p-3 bg-muted/30">
                    {project.images.map((img, i) =>
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`w-16 h-12 rounded-md overflow-hidden border-2 transition-colors flex-shrink-0 ${i === activeImage ? 'border-primary' : 'border-transparent'}`}>
                    
                        <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
                      </button>
                  )}
                  </div>
                }
              </div>

              {/* Description */}
              <div className="bg-white rounded-xl border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-3">About This Project</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{project.description}</p>
                <div className="flex flex-wrap gap-3 mt-4">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin size={12} />
                    {project.location}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Building2 size={12} />
                    {project.type}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Users size={12} />
                    {project.investors} investors
                  </div>
                </div>
              </div>

              {/* Milestone Timeline */}
              <div className="bg-white rounded-xl border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-5">Milestone Timeline</h2>
                <div className="space-y-3">
                  {project.milestones.map((milestone, i) =>
                  <div key={i} className="flex items-center gap-4 p-4 rounded-lg border border-border bg-muted/20">
                      <div className="w-8 h-8 rounded-full border-2 border-border bg-white flex items-center justify-center flex-shrink-0">
                        {milestone.status === 'Released' ?
                      <CheckCircle size={16} className="text-secondary" /> :

                      <Clock size={16} className="text-muted-foreground" />
                      }
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="text-sm font-medium text-foreground">{milestone.title}</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${getMilestoneColor(milestone.status)}`}>
                            {milestone.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-muted-foreground">{milestone.percentage}% of total</span>
                          <span className="text-xs text-muted-foreground">·</span>
                          <span className="text-xs text-muted-foreground">{milestone.date}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Documents */}
              <div className="bg-white rounded-xl border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-5">Project Documents</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {project.documents.map((doc, i) =>
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors cursor-pointer">
                      <div className="w-9 h-9 rounded-lg bg-primary/8 border border-primary/20 flex items-center justify-center flex-shrink-0">
                        <FileText size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{doc.name}</p>
                        <p className="text-xs text-muted-foreground">{doc.type}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-5">
              {/* Funding Card */}
              <div className="bg-white rounded-xl border border-border p-6 sticky top-20">
                <div className="mb-5">
                  <h1 className="text-xl font-bold text-foreground mb-1">{project.name}</h1>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <MapPin size={13} />
                    {project.location}
                  </div>
                </div>

                <div className="mb-5">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Funding Progress</span>
                    <span className="font-bold text-foreground">{progress}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden mb-3">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Raised</p>
                      <p className="font-bold text-foreground">{formatCurrency(project.fundedAmount)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Target</p>
                      <p className="font-bold text-foreground">{formatCurrency(project.totalAmount)}</p>
                    </div>
                  </div>
                </div>

                <Link
                  href="/sign-up-login-screen"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-md bg-primary text-white text-sm font-semibold hover:bg-primary/90 active:scale-95 transition-all duration-150 mb-3">
                  
                  Invest / Fund This Project
                </Link>
                <p className="text-xs text-muted-foreground text-center">Login or create an account to invest</p>
              </div>

              {/* Escrow Status */}
              <div className="bg-white rounded-xl border border-border p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Shield size={16} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Escrow Status</h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{project.escrowStatus}</p>
                <div className="mt-3 p-3 rounded-lg bg-secondary/5 border border-secondary/20">
                  <p className="text-xs text-secondary font-medium">✓ Funds are escrow-protected</p>
                </div>
              </div>

              {/* Developer Profile */}
              <div className="bg-white rounded-xl border border-border p-5">
                <h3 className="text-sm font-semibold text-foreground mb-4">Developer</h3>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                    <Building2 size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{project.developer.name}</p>
                    <p className="text-xs text-muted-foreground">{project.developer.company}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {project.developer.verified &&
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary/8 text-secondary border border-secondary/20">
                      <CheckCircle size={10} />
                      Verified
                    </span>
                  }
                  <span className="text-xs text-muted-foreground">{project.developer.projects} projects</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <HomepageFooter />
    </div>);

}